import { formatText, type Label, type Locale } from "../texts";
import { dateIn, dayIn } from "./organizations";

// Datos y reglas de las pantallas de Mensajería (R6), sin React: se prueban en messaging.test.tsx.

export type MessagingTab = "inboxes" | "channels" | "rules" | "conversations" | "simulator";

/** Pestañas de Mensajería (§2.3) con su primera pantalla. */
export const messagingTabs: { key: MessagingTab; labelKey: string; path: string }[] = [
  { key: "inboxes", labelKey: "ADM_MSG_TAB_INBOXES", path: "/Organizations/Bandejas" },
  { key: "channels", labelKey: "ADM_MSG_TAB_CHANNELS", path: "/Organizations/Canales" },
  { key: "rules", labelKey: "ADM_MSG_TAB_RULES", path: "/Organizations/Reglas" },
  { key: "conversations", labelKey: "ADM_MSG_TAB_CONVERSATIONS", path: "/Organizations/Conversaciones" },
  { key: "simulator", labelKey: "ADM_MSG_TAB_SIMULATOR", path: "/Organizations/Simulator" },
];

export type Inbox = {
  id: number;
  name: string;
  description: string | null;
  fallback: boolean;
  active: boolean;
  mode: string;
  members: number;
  open: number;
};

export type Channel = {
  id: number;
  name: string;
  type: string;
  phone: string | null;
  simulation: boolean;
  active: boolean;
  defaultInboxId: number | null;
  defaultInbox: string | null;
  /** Null si el conteo no respondió (la celda dice «—», nunca 0). */
  rules: number | null;
};

export type InboxOption = { id: number; name: string };

export type Rule = {
  id: number;
  source: string;
  headline: string | null;
  inboxId: number;
  inbox: string | null;
  active: boolean;
};

export type Origin = {
  source: string;
  headline: string | null;
  seen: number;
  lastSeen: string | null;
  ruleId: number | null;
  inbox: string | null;
};

/** Reparto de una bandeja en palabras (como la captura: Por turnos · Equilibrado · Manual · Persona específica). */
export function modeText(L: Label, mode: string): string {
  switch (mode) {
    case "ROUND_ROBIN":
      return L("ADM_MSG_MODE_TURNS");
    case "BALANCED":
      return L("ADM_MSG_MODE_BALANCED");
    case "DIRECT":
      return L("ADM_MSG_MODE_DIRECT");
    default:
      return L("ADM_MSG_MODE_MANUAL");
  }
}

/** «WhatsApp · +51 900 111 200» o «WhatsApp · Sin número»: el canal con su número, nunca inventado (E27). */
export function channelLine(L: Label, channel: Pick<Channel, "type" | "phone">): string {
  const type = channel.type === "WHATSAPP" ? L("CHTYPE_WHATSAPP") : L("ADM_MSG_CHANNEL_OTHER");
  return `${type} · ${channel.phone ?? L("ADM_MSG_NO_NUMBER")}`;
}

/** Nombre del canal en el selector de Reglas: «Ventas Lima · +51 900 111 200». */
export const channelOption = (channel: Pick<Channel, "name" | "phone">) =>
  channel.phone ? `${channel.name} · ${channel.phone}` : channel.name;

/** Origen de una regla o de un anuncio: el titular si se conoce; si no, el identificador tal como llega. */
export const originTitle = (item: { headline: string | null; source: string }) => item.headline ?? item.source;

export type ConversationRow = { id: number; title: string; kind: string; participants: number; open: boolean };

export const conversationFilters = ["ALL", "OPEN", "CLOSED"] as const;

export const kindText = (L: Label, kind: string) =>
  L(kind === "AGENT_CHAT" ? "ADM_MSG_KIND_AGENT" : kind === "DIRECT_CHAT" ? "ADM_MSG_KIND_DIRECT" : "ADM_MSG_KIND_GROUP");

export const conversationTabs = ["mensajes", "participantes", "auditoria"] as const;
export type ConversationTab = (typeof conversationTabs)[number];

export const normalizeConversationTab = (value: string | null): ConversationTab =>
  (conversationTabs as readonly string[]).includes(value ?? "") ? (value as ConversationTab) : "mensajes";

export const conversationHref = (orgId: number, caseId: number, tab: ConversationTab = "mensajes") =>
  `/Organizations/Conversacion?id=${orgId}&caseId=${caseId}&tab=${tab}`;

export type ConversationEvent = {
  id: number;
  at: string;
  action: string;
  subject: string | null;
  who: string | null;
  platform: boolean;
};

/** D7 · lo que pasó en palabras («Agregó a Valeria»), nunca el código del evento ni su JSON. */
export function eventText(L: Label, event: Pick<ConversationEvent, "action" | "subject">): string {
  switch (event.action) {
    case "GROUP_CREATED":
      return L("ADM_MSG_EV_CREATED");
    case "AGENT_CHAT_OPENED":
    case "DIRECT_CHAT_OPENED":
      return L("ADM_MSG_EV_OPENED");
    case "GROUP_RENAMED":
      return L("ADM_MSG_EV_RENAMED");
    case "GROUP_CLOSED":
      return L("ADM_MSG_EV_CLOSED");
    case "PARTICIPANT_ADDED":
      return event.subject ? formatText(L("ADM_MSG_EV_ADDED"), event.subject) : L("ADM_MSG_EV_ADDED_SOMEONE");
    case "PARTICIPANT_REMOVED":
      return event.subject ? formatText(L("ADM_MSG_EV_REMOVED"), event.subject) : L("ADM_MSG_EV_REMOVED_SOMEONE");
    default:
      return L("ADM_MSG_EV_OTHER");
  }
}

/** Quién hizo algo: la persona, «Soporte de Bheli» (Platform Admin) o «—» si su nombre no se pudo leer. */
export const eventWho = (L: Label, event: Pick<ConversationEvent, "who" | "platform">) =>
  event.platform ? L("ADM_MSG_WHO_PLATFORM") : (event.who ?? "—");

export const simChatHref = (orgId: number, contactId: number, sessionId?: number | null) =>
  `/Organizations/Chat?id=${orgId}&contactId=${contactId}${sessionId ? `&sessionId=${sessionId}` : ""}`;

/** Hora hh:mm en la zona de la empresa (D12). */
export function timeIn(locale: Locale, iso: string, zone: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  try {
    return new Intl.DateTimeFormat(locale, { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: zone }).format(
      date,
    );
  } catch {
    return new Intl.DateTimeFormat(locale, { hour: "2-digit", minute: "2-digit", hour12: false }).format(date);
  }
}

/** Separador de día de una conversación (C24): «Hoy», «Ayer», el día de la semana o la fecha completa. */
export function dayLabel(L: Label, locale: Locale, iso: string, zone: string, now = new Date()): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  let tz = zone;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: zone });
  } catch {
    tz = "America/Lima";
  }
  const day = dayIn(date, tz);
  const today = dayIn(now, tz);
  if (day === today) return L("ADM_MSG_TODAY");
  if (day === dayIn(new Date(now.getTime() - 86400000), tz)) return L("ADM_MSG_YESTERDAY");
  const days = (Date.parse(today) - Date.parse(day)) / 86400000;
  if (days > 1 && days < 7) {
    const weekday = new Intl.DateTimeFormat(locale, { weekday: "long", timeZone: tz }).format(date);
    return weekday.charAt(0).toUpperCase() + weekday.slice(1);
  }
  return dateIn(locale, iso, tz, day.slice(0, 4) !== today.slice(0, 4));
}

/** Mensajes en grupos por día, en orden (el separador de día va antes de cada grupo). */
export function byDay<T extends { at: string }>(
  L: Label,
  locale: Locale,
  messages: T[],
  zone: string,
): { day: string; items: T[] }[] {
  const groups: { day: string; items: T[] }[] = [];
  for (const message of messages) {
    const day = dayLabel(L, locale, message.at, zone);
    const last = groups[groups.length - 1];
    if (last && last.day === day) last.items.push(message);
    else groups.push({ day, items: [message] });
  }
  return groups;
}

/** Un cliente simulado; `testNumber` (RC1 · AF9): su número entre los de la empresa con el mismo nombre, o null. */
export type SimClientRow = {
  contactId: number | null;
  name: string;
  sessions: number;
  lastActivity: string | null;
  testNumber?: number | null;
};

export type SimSession = {
  id: number;
  number: number;
  open: boolean;
  mode: string;
  inbox: string | null;
  hasMore: boolean;
  messages: SimMessage[];
};

export type SimMessage = { id: number; from: "customer" | "ai" | "person" | "system"; text: string | null; at: string };

/** Quién responde en la sesión: IA, una persona, en espera o detenida (las palabras de hoy). */
export function responderText(L: Label, mode: string): string {
  switch (mode) {
    case "AI":
      return L("SIM_MODE_AI");
    case "HUMAN":
      return L("ADM_MSG_RESPONDER_PERSON");
    case "STOPPED":
      return L("SIM_MODE_STOPPED");
    default:
      return L("SIM_MODE_PENDING");
  }
}

/** Estado de una sesión simulada en una etiqueta: «Sesión 3 · abierta». */
export const sessionText = (L: Label, number: number, open: boolean) =>
  formatText(L(open ? "ADM_MSG_SESSION_OPEN" : "ADM_MSG_SESSION_CLOSED"), number);

/** Duración de una ejecución: «1.4 s» o «850 ms» (sin dato, nada). */
export function duration(locale: Locale, ms: number | null | undefined): string | null {
  if (ms == null || ms < 0) return null;
  if (ms < 1000) return `${Math.round(ms).toLocaleString(locale)} ms`;
  return `${(ms / 1000).toLocaleString(locale, { maximumFractionDigits: 1 })} s`;
}
